import { $api } from "@/kibi/core/utils/api";
import type {
  PaginatedResponse,
  QueryOptions,
} from "@/kibi/core/utils/pagination_types";
import type {
  CredentialDTO,
  UserDTO,
} from "@/kibi/modules/auth/data/models/auth_model";
import type { InjectionKey } from "vue";
import type {
  ChangePasswordRequestDTO,
  EmailHistoryDTO,
  GenerateRecoveryCodesRequestDTO,
  NotificationSettingsDTO,
  ResendEmailChangeOTPRequestDTO,
  TwoFaSecretDTO,
  UpdateEmailRequestDTO,
  UpdateEmailResponseDTO,
  UpdateProfileRequestDTO,
  UpdateTwoFaStatusRequestDTO,
  VerifyEmailRequestDTO,
  VerifyTotpRequestDTO,
} from "../models/profile_model";

export interface ProfileDataSource {
  updateProfile(request: UpdateProfileRequestDTO): Promise<UserDTO>;
  updateEmail(request: UpdateEmailRequestDTO): Promise<UpdateEmailResponseDTO>;
  changePassword(request: ChangePasswordRequestDTO): Promise<void>;
  getNotificationSettings(): Promise<NotificationSettingsDTO>;
  updateNotificationSettings(settings: NotificationSettingsDTO): Promise<void>;
  updateProfilePhoto(photo: File): Promise<string>;
  removeProfilePhoto(): Promise<void>;
  verifyEmail(request: VerifyEmailRequestDTO): Promise<CredentialDTO>;
  getEmailPendingVerification(): Promise<string>;
  resendEmailChangeOTP(request: ResendEmailChangeOTPRequestDTO): Promise<void>;
  getEmailHistory(
    userId: string,
    options: QueryOptions,
  ): Promise<PaginatedResponse<EmailHistoryDTO[]>>;
  updateTwoFaStatus(request: UpdateTwoFaStatusRequestDTO): Promise<void>;
  generateTotpSecret(): Promise<TwoFaSecretDTO>;
  verifyTotpSetup(request: VerifyTotpRequestDTO): Promise<Blob>;
  cancelTotpSetup(): Promise<void>;
  generateRecoveryCodes(
    request: GenerateRecoveryCodesRequestDTO,
  ): Promise<Blob>;
}

export const profileDataSourceKey: InjectionKey<ProfileDataSource> =
  Symbol("ProfileDataSource");

export class ProfileDataSourceImpl implements ProfileDataSource {
  async updateProfile(request: UpdateProfileRequestDTO): Promise<UserDTO> {
    const response = await $api<{ message: string; data: UserDTO }>(
      "/accounts/profile",
      {
        method: "PUT",
        body: request,
      },
    );
    return response.data;
  }

  async updateEmail(
    request: UpdateEmailRequestDTO,
  ): Promise<UpdateEmailResponseDTO> {
    const response = await $api<{
      message: string;
      data: UpdateEmailResponseDTO;
    }>("/accounts/email", {
      method: "PUT",
      body: request,
    });
    return response.data;
  }

  async changePassword(request: ChangePasswordRequestDTO): Promise<void> {
    await $api("/accounts/password", {
      method: "PUT",
      body: request,
    });
  }

  async getNotificationSettings(): Promise<NotificationSettingsDTO> {
    const response = await $api<{
      message: string;
      data: NotificationSettingsDTO;
    }>("/accounts/notification-settings", {
      method: "GET",
    });
    return response.data;
  }

  async updateNotificationSettings(
    settings: NotificationSettingsDTO,
  ): Promise<void> {
    await $api("/accounts/notification-settings", {
      method: "PUT",
      body: settings,
    });
  }

  async updateProfilePhoto(photo: File): Promise<string> {
    const formData = new FormData();
    formData.append("photo", photo);

    const response = await $api<{ message: string; data: string }>(
      "/accounts/profile-photo",
      {
        method: "PUT",
        body: formData,
      },
    );

    return response.data;
  }

  async removeProfilePhoto(): Promise<void> {
    await $api("/accounts/profile-photo", {
      method: "DELETE",
    });
  }

  async verifyEmail(request: VerifyEmailRequestDTO): Promise<CredentialDTO> {
    const response = await $api<{ message: string; data: CredentialDTO }>(
      "/accounts/verify-email",
      {
        method: "POST",
        body: request,
      },
    );
    return response.data;
  }

  async getEmailPendingVerification(): Promise<string> {
    const response = await $api<{ message: string; data: { email: string } }>(
      "/accounts/email-pending-verification",
      {
        method: "GET",
      },
    );
    return response.data.email;
  }

  async resendEmailChangeOTP(
    request: ResendEmailChangeOTPRequestDTO,
  ): Promise<void> {
    await $api("/accounts/resend-email-change-otp", {
      method: "POST",
      body: request,
    });
  }

  async getEmailHistory(
    userId: string,
    options: QueryOptions,
  ): Promise<PaginatedResponse<EmailHistoryDTO[]>> {
    const response = await $api<PaginatedResponse<EmailHistoryDTO[]>>(
      `/emails/users/${userId}/history`,
      {
        method: "GET",
        params: options,
      },
    );
    return response;
  }

  async updateTwoFaStatus(request: UpdateTwoFaStatusRequestDTO): Promise<void> {
    await $api("/accounts/2fa/status", {
      method: "PUT",
      body: request,
    });
  }

  async generateTotpSecret(): Promise<TwoFaSecretDTO> {
    const response = await $api<{ message: string; data: TwoFaSecretDTO }>(
      "/accounts/2fa/totp/secret",
      {
        method: "POST",
      },
    );
    return response.data;
  }

  async verifyTotpSetup(request: VerifyTotpRequestDTO): Promise<Blob> {
    const response = await ($api as any)("/accounts/2fa/totp/verify", {
      method: "POST",
      body: request,
      responseType: "blob",
    });
    return response;
  }

  async cancelTotpSetup(): Promise<void> {
    await $api("/accounts/2fa/totp/cancel", {
      method: "POST",
    });
  }

  async generateRecoveryCodes(
    request: GenerateRecoveryCodesRequestDTO,
  ): Promise<Blob> {
    const response = await ($api as any)("/accounts/2fa/totp/recovery-codes", {
      method: "POST",
      body: request,
      responseType: "blob",
    });
    return response;
  }
}
